import { useEffect, useRef } from 'preact/hooks';
import { Program } from '../render/gl';

// A small interactive globe for the Sun, Moon and planets: real surface maps, lit as the body
// appears from Earth right now (phase and tilt), slowly turning; drag to spin it.

const VS = /* glsl */ `#version 300 es
layout(location = 0) in vec2 a_pos;
out vec2 v_q;
uniform float u_extent;
void main() { v_q = a_pos * u_extent; gl_Position = vec4(a_pos, 0.0, 1.0); }`;

const FS = /* glsl */ `#version 300 es
precision highp float;
in vec2 v_q;
out vec4 o;
uniform sampler2D u_tex;
uniform sampler2D u_ring;
uniform int u_kind;        // 0 planet, 1 Sun, 2 Moon
uniform int u_hasRing;
uniform vec3 u_light;      // towards the Sun, view frame (x right, y up, z to viewer)
uniform vec3 u_pole;       // body north pole, view frame
uniform float u_spin;      // longitude offset, radians
uniform float u_px;        // pixels per unit radius (for anti-aliasing)
const float PI = 3.14159265;

vec3 surface(vec3 n) {
  vec3 e1 = normalize(u_pole);
  vec3 e2 = normalize(vec3(0.0, 0.0, 1.0) - e1 * e1.z);
  vec3 e3 = cross(e1, e2);
  float lon = atan(dot(n, e3), dot(n, e2)) + u_spin;
  float lat = asin(clamp(dot(n, e1), -1.0, 1.0));
  vec3 c = texture(u_tex, vec2(fract(0.5 + lon / (2.0 * PI)), 0.5 - lat / PI)).rgb;
  return pow(c, vec3(2.2));
}

void main() {
  float r = length(v_q);
  vec3 col = vec3(0.0);
  float a = 0.0;
  float aa = 1.5 / u_px;
  bool onDisc = r < 1.0 + aa;
  float z = sqrt(max(0.0, 1.0 - r * r));
  vec3 n = vec3(v_q, z);
  if (onDisc) {
    vec3 albedo = surface(n);
    float lit;
    if (u_kind == 1) lit = 1.6 * (0.45 + 0.55 * pow(z, 0.5));
    else if (u_kind == 2) { float mu0 = dot(n, u_light); lit = mu0 > 0.0 ? 2.2 * mu0 / (mu0 + z + 1e-3) : 0.0; lit += 0.02; }
    else { float mu0 = dot(n, u_light); lit = max(mu0, 0.0) * 1.35 + 0.02; }
    col = albedo * lit;
    // Soft atmosphere rim for the gas giants and Venus.
    if (u_kind == 0) col += vec3(0.25, 0.3, 0.4) * pow(1.0 - z, 3.0) * max(dot(n, u_light) + 0.3, 0.0) * 0.4;
    a = clamp((1.0 - r) / aa + 0.5, 0.0, 1.0);
  }
  if (u_hasRing == 1 && abs(u_pole.z) > 1e-3) {
    vec3 P = normalize(u_pole);
    float zr = -(P.x * v_q.x + P.y * v_q.y) / P.z;
    vec3 pr = vec3(v_q, zr);
    float rr = length(pr);
    bool hidden = r < 1.0 && zr < z;
    if (rr > 1.24 && rr < 2.27 && !hidden) {
      vec4 ring = texture(u_ring, vec2((rr - 1.24) / (2.27 - 1.24), 0.5));
      float ra = ring.a * 0.95;
      // The planet's shadow on the rings (the Sun's direction is roughly perpendicular).
      vec3 L = normalize(u_light);
      float t = -dot(pr, L);
      vec3 closest = pr + L * t;
      float shade = (t > 0.0 && length(closest) < 1.0) ? 0.15 : 1.0;
      float ringLit = 0.35 + 0.65 * abs(dot(P, L));
      col = mix(col, pow(ring.rgb, vec3(2.2)) * 1.4 * ringLit * shade, ra);
      a = max(a, ra);
    }
  }
  col = col / (1.0 + col * 0.35);
  col = pow(col, vec3(1.0 / 2.2));
  o = vec4(col * a, a);
}`;

export interface GlobeProps {
  texture: string;
  kind: 0 | 1 | 2;
  light: [number, number, number];
  pole: [number, number, number];
  spin?: number; // initial longitude offset (radians)
  rotate?: boolean;
  ring?: string;
  size?: number;
}

export function Globe({ texture, kind, light, pole, spin = 0, rotate = true, ring, size = 168 }: GlobeProps) {
  const ref = useRef<HTMLCanvasElement>(null);
  const state = useRef({ spin, drag: null as null | { x: number; spin: number }, light, pole });
  state.current.light = light;
  state.current.pole = pole;
  useEffect(() => {
    const canvas = ref.current!;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const extent = ring ? 2.35 : 1.08;
    canvas.width = canvas.height = Math.round(size * dpr);
    const gl = canvas.getContext('webgl2', { premultipliedAlpha: true, alpha: true, antialias: false });
    if (!gl || gl.isContextLost()) return;
    const prog = new Program(gl, VS, FS, 'globe');
    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    const b = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, b);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    const load = (url: string, unit: number) =>
      new Promise<void>((res) => {
        const img = new Image();
        img.onload = () => {
          const t = gl.createTexture()!;
          gl.activeTexture(gl.TEXTURE0 + unit);
          gl.bindTexture(gl.TEXTURE_2D, t);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
          gl.generateMipmap(gl.TEXTURE_2D);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
          res();
        };
        img.onerror = () => res();
        img.src = url;
      });
    let raf = 0;
    let alive = true;
    let last = performance.now();
    Promise.all([load(texture, 0), ring ? load(ring, 1) : Promise.resolve()]).then(() => {
      const frame = (t: number) => {
        if (!alive) return;
        const dt = Math.min(0.1, (t - last) / 1000);
        last = t;
        if (rotate && !state.current.drag) state.current.spin += dt * 0.25;
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        prog.use();
        prog.f1('u_extent', extent).i1('u_tex', 0).i1('u_ring', 1).i1('u_kind', kind).i1('u_hasRing', ring ? 1 : 0);
        prog.v3('u_light', state.current.light).v3('u_pole', state.current.pole).f1('u_spin', state.current.spin).f1('u_px', canvas.width / 2 / extent);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        raf = requestAnimationFrame(frame);
      };
      raf = requestAnimationFrame(frame);
    });
    const down = (e: PointerEvent) => {
      canvas.setPointerCapture(e.pointerId);
      state.current.drag = { x: e.clientX, spin: state.current.spin };
    };
    const move = (e: PointerEvent) => {
      const d = state.current.drag;
      if (d) state.current.spin = d.spin - ((e.clientX - d.x) / size) * Math.PI;
    };
    const up = () => (state.current.drag = null);
    canvas.addEventListener('pointerdown', down);
    canvas.addEventListener('pointermove', move);
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', up);
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, [texture, ring, kind]);
  // A new canvas for each body: the old one's context is let go on cleanup, and a canvas can never get another.
  return <canvas key={`${texture} ${ring} ${kind}`} ref={ref} class="globe" style={{ width: `${size}px`, height: `${size}px` }} aria-label="3D model" />;
}
